/* ============================================
   Artemis Theme — Global JavaScript
   ============================================ */

(function () {
  'use strict';

  /* --- Scroll-triggered Reveal Animations --- */
  function initRevealAnimations() {
    var reveals = document.querySelectorAll('.reveal, .stagger-children');
    if (!reveals.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    reveals.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* --- Sticky Header --- */
  function initStickyHeader() {
    var header = document.querySelector('.header');
    if (!header) return;

    var scrollThreshold = 50;
    var lastScroll = 0;

    function onScroll() {
      var currentScroll = window.pageYOffset;

      if (currentScroll > scrollThreshold) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- Desktop Nav Dropdowns --- */
  function initNavDropdowns() {
    var toggles = document.querySelectorAll('[data-dropdown-toggle]');
    if (!toggles.length) return;

    function closeDropdown(toggle) {
      var item = toggle.closest('.header__nav-item');
      if (item) item.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    function closeAll(except) {
      toggles.forEach(function (toggle) {
        if (toggle !== except) closeDropdown(toggle);
      });
    }

    toggles.forEach(function (toggle) {
      var item = toggle.closest('.header__nav-item');
      if (!item) return;

      toggle.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        closeAll(toggle);
        if (isOpen) {
          closeDropdown(toggle);
        } else {
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });

      item.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && item.classList.contains('is-open')) {
          closeDropdown(toggle);
          toggle.focus();
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.header__nav-item')) closeAll(null);
    });
  }

  /* --- Mobile Menu Toggle --- */
  function initMobileMenu() {
    var toggle = document.querySelector('[data-mobile-menu-toggle]');
    var drawer = document.querySelector('[data-mobile-menu]');
    var overlay = document.querySelector('[data-overlay]');
    var closeBtn = document.querySelector('[data-mobile-menu-close]');
    var background = document.querySelector('.header__inner');

    if (!toggle || !drawer) return;

    function openMenu() {
      drawer.classList.add('is-active');
      if (overlay) overlay.classList.add('is-active');
      if (background) background.setAttribute('inert', '');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      trapFocus(drawer);
    }

    function closeMenu() {
      drawer.classList.remove('is-active');
      if (overlay) overlay.classList.remove('is-active');
      if (background) background.removeAttribute('inert');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      toggle.focus();
    }

    toggle.addEventListener('click', function () {
      var isOpen = drawer.classList.contains('is-active');
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', closeMenu);
    }

    if (overlay) {
      overlay.addEventListener('click', closeMenu);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) {
        closeMenu();
      }
    });
  }

  /* --- Focus Trap --- */
  function trapFocus(element) {
    var focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) return;

    var firstFocusable = focusableElements[0];
    var lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable.focus();

    element.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    });
  }

  /* --- Announcement Bar Dismiss --- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement-bar]');
    var dismiss = document.querySelector('[data-announcement-dismiss]');

    if (!bar || !dismiss) return;

    dismiss.addEventListener('click', function () {
      bar.style.height = bar.offsetHeight + 'px';
      requestAnimationFrame(function () {
        bar.style.height = '0';
        bar.style.overflow = 'hidden';
        bar.style.transition = 'height 0.3s ease';
      });

      try {
        sessionStorage.setItem('artemis-announcement-dismissed', 'true');
      } catch (e) {}
    });

    try {
      if (sessionStorage.getItem('artemis-announcement-dismissed') === 'true') {
        bar.style.display = 'none';
      }
    } catch (e) {}
  }

  /* --- Smooth Scroll for Anchor Links --- */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href');
      if (targetId === '#') return;

      var target = document.querySelector(targetId);
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth' });
    });
  }

  /* --- Quantity Selector --- */
  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quantity-btn]');
      if (!btn) return;

      var wrapper = btn.closest('[data-quantity-selector]');
      if (!wrapper) return;

      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      var currentValue = parseInt(input.value, 10) || 1;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 9999;
      // Products sold in multiples carry a step. The buttons have to move by
      // it, or they walk the customer onto a quantity the input's own
      // validation then rejects on submit.
      var step = parseInt(input.getAttribute('step'), 10) || 1;
      var action = btn.getAttribute('data-quantity-btn');

      if (action === 'minus' && currentValue > min) {
        input.value = Math.max(min, currentValue - step);
      } else if (action === 'plus' && currentValue < max) {
        input.value = Math.min(max, currentValue + step);
      }

      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* --- Initialize All --- */
  function init() {
    initRevealAnimations();
    initStickyHeader();
    initNavDropdowns();
    initMobileMenu();
    initAnnouncementBar();
    initSmoothScroll();
    initQuantitySelectors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
